import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Megaphone, Save, CheckCircle2, ExternalLink } from 'lucide-react';
import { useAdsConfig } from '../context/AdsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AdsSettingsPanel() {
  const { refresh } = useAdsConfig();
  const [config, setConfig] = useState({
    enabled: false,
    publisher_id: '',
    slot_top: '',
    slot_sidebar: '',
    slot_inline: '',
    slot_bottom: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const { data } = await axios.get(`${API}/settings/ads`);
      setConfig(data);
    } catch (err) {
      console.error('Error fetching ads config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConfig(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setSaving(true);
    try {
      await axios.put(`${API}/settings/ads`, config, { withCredentials: true });
      setSuccess(true);
      refresh();
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save ads config');
    } finally {
      setSaving(false);
    }
  };

  const slots = [
    { name: 'slot_top', label: 'Top Banner Slot ID', description: 'Above hero section (728×90 leaderboard recommended)' },
    { name: 'slot_sidebar', label: 'Sidebar Slot ID', description: 'Beside Latest Updates (300×250 medium rectangle)' },
    { name: 'slot_inline', label: 'Inline Slot ID', description: 'Between category sections (responsive)' },
    { name: 'slot_bottom', label: 'Bottom Banner Slot ID', description: 'Above footer (728×90 leaderboard)' }
  ];

  if (loading) {
    return (
      <div className="bg-white border border-[#E5E5E5] p-6" data-testid="ads-settings-loading">
        <div className="text-[#8A8A8A]">Loading ads config...</div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E5E5E5] p-6" data-testid="ads-settings-panel">
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 bg-[#FFB703] bg-opacity-20 border border-[#FFB703]">
          <Megaphone size={18} className="text-[#FFB703]" />
        </div>
        <h2 className="text-2xl font-bold tracking-tight text-[#1A1A2E]">
          Google AdSense
        </h2>
      </div>
      <p className="text-sm text-[#8A8A8A] mb-6">
        Connect your AdSense account once approved. Get your Publisher ID and Slot IDs from{' '}
        <a
          href="https://adsense.google.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#E63946] hover:underline inline-flex items-center gap-1"
          data-testid="adsense-portal-link"
        >
          adsense.google.com
          <ExternalLink size={12} />
        </a>
      </p>

      <form onSubmit={handleSubmit} className="space-y-5">
        <label className="flex items-center gap-3 p-4 border-2 border-[#E5E5E5] hover:border-[#FFB703] transition-colors cursor-pointer">
          <input
            type="checkbox"
            name="enabled"
            checked={config.enabled}
            onChange={handleChange}
            data-testid="ads-enabled-checkbox"
            className="w-5 h-5"
          />
          <div>
            <div className="text-sm font-bold text-[#1A1A2E]">Enable Google Ads</div>
            <div className="text-xs text-[#8A8A8A]">When off, placeholders are shown but no real ads load.</div>
          </div>
        </label>

        <div>
          <label className="block text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-2">
            PUBLISHER ID
          </label>
          <input
            type="text"
            name="publisher_id"
            value={config.publisher_id}
            onChange={handleChange}
            data-testid="ads-publisher-id-input"
            placeholder="ca-pub-1234567890123456"
            className="w-full px-4 py-3 border border-[#E5E5E5] focus:outline-none focus:border-[#FFB703] transition-colors font-mono"
          />
          <p className="text-xs text-[#8A8A8A] mt-1">Starts with "ca-pub-" — found in your AdSense account home.</p>
        </div>

        <div className="border-t border-[#E5E5E5] pt-5">
          <h3 className="text-sm font-bold text-[#1A1A2E] mb-4">Ad Slot IDs</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {slots.map(({ name, label, description }) => (
              <div key={name}>
                <label className="block text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-1">
                  {label}
                </label>
                <input
                  type="text"
                  name={name}
                  value={config[name]}
                  onChange={handleChange}
                  data-testid={`ads-${name.replace('_', '-')}-input`}
                  placeholder="1234567890"
                  className="w-full px-3 py-2 border border-[#E5E5E5] focus:outline-none focus:border-[#FFB703] transition-colors font-mono text-sm"
                />
                <p className="text-[10px] text-[#8A8A8A] mt-1">{description}</p>
              </div>
            ))}
          </div>
        </div>

        {error && (
          <div className="p-3 bg-[#E63946] bg-opacity-10 border border-[#E63946] text-[#E63946] text-sm" data-testid="ads-settings-error">
            {error}
          </div>
        )}

        {success && (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-500 text-green-700 text-sm" data-testid="ads-settings-success">
            <CheckCircle2 size={16} />
            Ads configuration saved successfully.
          </div>
        )}

        <button
          type="submit"
          disabled={saving}
          data-testid="ads-settings-save-button"
          className="flex items-center gap-2 px-6 py-3 bg-[#FFB703] text-[#1A1A2E] font-bold text-sm tracking-[0.2em] uppercase hover:bg-[#e6a503] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save size={16} />
          {saving ? 'SAVING...' : 'SAVE ADS CONFIG'}
        </button>
      </form>
    </div>
  );
}

export default AdsSettingsPanel;
